<template>
  <div class="p-2 max-w-7xl mx-auto space-y-6 text-base-content bg-base-50 min-h-screen">
    <div v-if="workOrder">
      <!-- HEADER ACTION BANNER -->
      <header
          class="flex flex-col md:flex-row justify-between items-start md:items-center border-gray-200 dark:border-gray-700 p-6 rounded-xl border  shadow-sm gap-4">
        <div>
          <div class="flex items-center gap-3">
            <h1 class="text-2xl font-bold tracking-tight">Work Order #{{ workOrder.id }}</h1>
            <span :class="['badge badge-md font-semibold font-mono uppercase', statusBadgeClass]">
            {{ workOrder.status ?? "draft" }}
          </span>
          </div>
          <p class="text-sm text-base-content/60 mt-1 font-medium">
            Asset: <span class="text-base-content font-semibold">{{ workOrder.machine?.name }}</span>
          </p>
        </div>

        <!-- Action State Machine Triggers -->
        <div class="flex flex-wrap gap-2 w-full md:w-auto">
          <!-- Start Work Button -->
          <button
              v-if="[null, 'draft'].includes(workOrder.status)"
              @click="updateStatus('planned')"
              class="btn btn-primary btn-sm md:btn-md shadow-sm"
          >
            ⚡ Confirm
          </button>
          <button
              v-if="['planned', 'confirmed'].includes(workOrder.status)"
              @click="updateStatus('in_progress')"
              class="btn btn-primary btn-sm md:btn-md shadow-sm"
          >
            ⚡ Start Work
          </button>

          <!-- Submit Completion Report Alert Button -->
          <button
              v-if="workOrder.status === 'in_progress' && allRequiredTasksDone"
              @click="openReportSection"
              class="btn btn-warning btn-sm md:btn-md shadow-sm"
          >
            📝 Submit Completion Report
          </button>

          <!-- Mark Work Order Completed Button -->
          <button
              v-if="workOrder.status === 'in_progress'"
              :disabled="!canCompleteWorkOrder"
              @click="updateStatus('completed')"
              class="btn btn-success btn-sm md:btn-md text-white shadow-sm"
          >
            ✓ Mark Work Order Completed
          </button>

          <!-- Approve / Validate Button -->
          <button
              v-if="workOrder.status === 'completed'"
              @click="updateStatus('validated')"
              class="btn btn-info btn-sm md:btn-md text-white shadow-sm"
          >
            🛡️ Approve / Validate
          </button>

          <!-- Close Button -->
          <button
              v-if="workOrder.status === 'validated'"
              @click="updateStatus('closed')"
              class="btn btn-neutral btn-sm md:btn-md shadow-sm"
          >
            📦 Close & Archive
          </button>
        </div>
      </header>
      <!-- LAYOUT SPLIT GRID CONTAINER -->
      <div class="grid  mt-6 grid-cols-1 lg:grid-cols-3 gap-6">

        <!-- LEFT AREA: LOGICAL ENGINE SHEETS -->
        <div class="lg:col-span-2 space-y-6">

          <!-- Context Card Section -->
          <section class="card border-gray-200 dark:border-gray-700 border shadow-sm">
            <div class="card-body p-6">
              <h2 class="card-title text-lg font-bold">Scope of Work</h2>
              <p class="text-sm leading-relaxed text-base-content/80">{{ workOrder.description }}</p>

              <div class="grid grid-cols-2 gap-4 mt-4 pt-4 border-ttext-xs">
                <div><span class="text-base-content/50 block font-medium">Assigned Team:</span>
                  <span
                      class="font-semibold"
                      v-for="m in workOrder.organization_members">{{ m.organization_member.full_name }}</span></div>
                <div><span class="text-base-content/50 block font-medium">Priority Code:</span> <span
                    class="font-bold text-error">{{ workOrder.priority }}</span></div>
              </div>
            </div>
          </section>

          <!-- Checklists Table Operations Section -->
          <section class="card border-gray-200 dark:border-gray-700 border shadow-sm">
            <div class="card-body p-6">
              <div class="flex justify-between items-center mb-2">
                <h2 class="card-title text-lg font-bold">Operations & Task Checklists</h2>

                <button
                    v-if="['planned', 'confirmed', 'in_progress'].includes(workOrder.status)"
                    @click="isModalOpen = true"
                    class="btn btn-outline btn-primary btn-xs sm:btn-sm"
                >
                  + Add Task
                </button>
                <div v-else class="badge badge-ghost text-base-content/40 font-semibold">{{ workOrder.status }}</div>
              </div>

              <div class="alert alert-info py-2 px-3 text-xs rounded-lg mb-4 flex items-center gap-2">
                <span>📊 Task Status: <strong>{{ completedTasksCount }} / {{ workOrder.tasks.length }}</strong> completed.</span>
              </div>

              <div class="overflow-x-auto">
                <table class="table table-sm w-full border-t border-base-100">
                  <thead>
                  <tr class="bg-base-200/50">
                    <th class="w-12 text-center">Status</th>
                    <th>Task Description</th>
                    <th>Assignee</th>
                    <th class="w-24 text-right">Action</th>
                  </tr>
                  </thead>
                  <tbody>
                  <tr v-for="task in workOrder.tasks" :key="task.id" class="hover:bg-base-200/30">
                    <td class="text-center">
                      <input type="checkbox" :checked="task.status === 'done'" disabled
                             class="checkbox checkbox-xs checkbox-success"/>
                    </td>
                    <td>
                      <div class="font-medium text-sm"
                           :class="{'line-through text-base-content/40': task.status === 'done'}">
                        {{ task.title }} {{ task.status }}
                      </div>
                      <span v-if="task.required"
                            class="badge badge-error badge-outline scale-75 origin-left font-bold uppercase tracking-wider text-[10px]">
                        Required
                      </span>
                    </td>
                    <td class="text-xs font-mono font-medium text-base-content/70">
                      <span class="badge" v-for="m in task.assignees">{{ m.name || m.full_name }}</span>
                      <div v-if="['open', 'doing' ].includes (task.status)">
                        <input class="range" type="range" min="1"
                               @change.prevent="updateTaskProgress(task.id, $event.currentTarget.value)" max="100"
                               :value="task.progress"/>
                      </div>
                    </td>
                    <td class="text-right">
                      <button
                          v-if="task.status === 'open'"
                          @click="_updateTaskStatus(task.id, 'doing')"
                          class="btn btn-info btn-xs text-white"
                      >
                        -> Start
                      </button>

                      <button
                          v-else-if="!(task.status === 'done') && ['doing'].includes(task.status)"
                          @click="completeTask(task.id)"
                          class="btn btn-success btn-xs text-white"
                      >
                        ✓ Finish
                      </button>
                      <span v-else-if="task.done" class="text-xs text-success font-semibold">Ready</span>
                      <span v-else class="text-xs text-base-content/30">—</span>
                    </td>
                  </tr>
                  <tr v-if="workOrder.tasks.length === 0">
                    <td colspan="4" class="text-center py-6 text-sm text-base-content/40 font-medium">No tasks assigned
                      to this profile yet.
                    </td>
                  </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </section>
          <!-- Completion Report Section -->
          <section ref="reportSection" class="card border-gray-200 dark:border-gray-700 border shadow-sm">
            <div class="card-body p-6 space-y-4">
              <div class="flex justify-between items-center">
                <h2 class="card-title text-lg font-bold">Work Order Completion Report</h2>
                <span v-if="workOrder?.report && workOrder.validated"
                      class="badge badge-success text-white font-semibold">Submitted</span>
                <span v-else class="badge badge-ghost text-base-content/40 font-semibold">Draft</span>
              </div>

              <div v-if="workOrder?.report?.submitted" class="p-4 bg-base-200/40 rounded-lg space-y-2 text-sm">
                <p><strong>Summary Notes:</strong> {{ workOrder.report?.summary }}</p>
                <p><strong>Signatures Present:</strong> {{ workOrder.report?.signaturePresent ? '✅ Yes' : '❌ No' }}</p>
              </div>

              <div v-else-if="workOrder.status === 'in_progress'" class="space-y-4">
                <div class="space-y-3">
                  <label class="label font-semibold text-xs text-base-content/70">Technical Resolution Notes</label>
                  <textarea
                      v-model="reportForm.summary"
                      class="textarea textarea-bordered h-24 w-full text-sm"
                      placeholder="Detail technical actions, measurements taken, and adjustments..."
                  ></textarea>
                </div>
                <div class="form-control space-y-3">
                  <label class="label font-semibold text-xs text-base-content/70">Work performed</label>
                  <textarea
                      v-model="reportForm.work_performed"
                      class="textarea textarea-bordered h-24  w-full text-sm"
                      placeholder="Detail technical actions, measurements taken, and adjustments..."
                  ></textarea>
                </div>
                <div class="space-y-3">
                  <label class="label font-semibold text-xs text-base-content/70">Cost</label>
                  <input
                      v-model="reportForm.cost"
                      class="input w-full  text-sm"
                      placeholder="$"
                  ></input>
                </div>
                <div class="form-control space-x-3">
                  <label class="label font-semibold text-xs text-base-content/70">Duration</label>
                  <input
                      v-model="reportForm.duration"
                      class="input w-16  text-sm"
                      placeholder="??"
                  ></input>
                  <select
                      v-model="reportForm.duration_unit"
                      class="select w-20 text-sm"
                  >
                    <option value="m">minutes</option>
                    <option value="h" selected>hours</option>
                    <option value="d">days</option>
                  </select>
                </div>

                <div class="form-control max-w-xs">
                  <label class="label cursor-pointer flex justify-start gap-3">
                    <input type="checkbox" v-model="reportForm.signaturePresent" class="checkbox"/>
                    <span
                        class="label-text font-medium text-sm">Verify signatures and checklist fields are matching</span>
                  </label>
                </div>

                <button
                    @click="submitReport"
                    :disabled="!allRequiredTasksDone || !reportForm.summary"
                    class="btn btn-warning btn-sm"
                >
                  {{ reportForm.id ? 'Update Report' : 'Submit' }}
                </button>
              </div>

              <div v-else class="text-sm text-base-content/40 italic font-medium">
                The completion report form becomes configurable when work operations move to an active state.
              </div>
            </div>
          </section>
        </div>

        <!-- RIGHT SIDEBAR COLUMN: RULES VERIFICATION CHECKLIST -->
        <div class="space-y-6">
          <section class="card border-gray-200 dark:border-gray-700 border shadow-sm sticky top-6">
            <div class="card-body p-6">
              <h3 class="font-bold text-lg mb-4">Completion Verification</h3>

              <ul class="space-y-3 text-sm font-medium">
                <li class="flex items-start gap-3">
                <span :class="workOrder.tasks.length > 0 ? 'text-success' : 'text-error'">
                  {{ workOrder.tasks.length > 0 ? '●' : '○' }}
                </span>
                  <span :class="{'line-through text-base-content/40': workOrder.tasks.length > 0}">
                  Has at least 1 task profile
                </span>
                </li>
                <li class="flex items-start gap-3">
                <span :class="allRequiredTasksDone ? 'text-success' : 'text-error'">
                  {{ allRequiredTasksDone ? '●' : '○' }}
                </span>
                  <span :class="{'line-through text-base-content/40': allRequiredTasksDone}">
                  All required tasks are completed
                </span>
                </li>
                <li class="flex items-start gap-3">
                <span :class="workOrder.report?.submitted ? 'text-success' : 'text-error'">
                  {{ workOrder.report?.submitted ? '●' : '○' }}
                </span>
                  <span :class="{'line-through text-base-content/40': workOrder.report?.submitted}">
                  Completion report exists & submitted
                </span>
                </li>
                <li class="flex items-start gap-3">
                <span :class="workOrder.report?.signaturePresent ? 'text-success' : 'text-error'">
                  {{ workOrder.report?.signaturePresent ? '●' : '○' }}
                </span>
                  <span :class="{'line-through text-base-content/40': workOrder.report?.signaturePresent}">
                  Signatures / checklists are present
                </span>
                </li>
              </ul>

              <div class="mt-6 pt-4 border-ttext-xs text-base-content/50 space-y-2">
                <p><strong>Completed:</strong> Work done + report submitted</p>
                <p><strong>Validated:</strong> Report approved by supervisor</p>
                <p><strong>Closed:</strong> Archived / admin finalized</p>
              </div>
            </div>
          </section>
        </div>
      </div>
      <!-- DAISYUI RE-ACTIVE MODAL CONTAINER -->
      <div :class="['modal', { 'modal-open': isModalOpen }]">
        <div
            class="modal-box max-w-md border-gray-200 dark:border-gray-700 rounded-xl shadow-lg border ">
          <h3 class="font-bold text-lg mb-4">Add Task Profile</h3>

          <div class="space-y-4">
            <div class="form-control">
              <label class="label font-semibold text-xs text-base-content/70">Task Title</label>
              <input
                  type="text"
                  v-model="newTaskForm.title"
                  class="input input-bordered w-full text-sm"
                  placeholder="e.g., Replace internal ring-seal gaskets"
              />
            </div>

            <div class="form-control">
              <label class="label font-semibold text-xs text-base-content/70">Assignee Name</label>
              <select
                  v-model="newTaskForm.assigneeId"
                  placeholder="Select assignee"
                  class="input input-bordered w-full text-sm"
              >
                <option v-for="m in orgMembers" v-bind:value="m.id">
                  {{ m.full_name }}
                </option>
              </select>
            </div>

            <div class="form-control">
              <label class="label cursor-pointer flex justify-start gap-3">
                <input type="checkbox" v-model="newTaskForm.required" class="checkbox checkbox-primary"/>
                <span class="label-text font-medium text-sm">Mark as critical (Required for Completion)</span>
              </label>
            </div>
          </div>

          <div class="modal-action">
            <button @click="closeModal" class="btn btn-ghost btn-sm">Cancel</button>
            <button
                @click="addTask"
                :disabled="!newTaskForm.title || !newTaskForm.assigneeId"
                class="btn btn-primary btn-sm"
            >
              Append Task
            </button>
          </div>
        </div>
        <div class="modal-backdrop" @click="closeModal"></div>
      </div>
    </div>
    <div v-else class=" justify-center items-center h-96">
      <div class="skeleton h-48  border border-gray-200 dark:border-gray-700 "></div>
      <div class="flex mt-2 space-x-4">
        <div class="skeleton bg-base-100 border   border-gray-200 dark:border-gray-700  w-3/4 h-96"></div>
        <div class="skeleton bg-base-100  border border-gray-200 bg dark:border-gray-700  flex-grow h-96"></div>
      </div>

    </div>
  </div>
</template>

<script setup>
import {ref, computed, reactive} from 'vue'
import {useRoute} from "vue-router";
import {fetchWorkOrder, updateWorkOrderStatus} from "~/lib/api/work_orders";
import {createTask, updateTask, updateTaskStatus} from "~/lib/api/tasks";
import {fetchMembers} from "~/lib/api/members";
import {UpsertWorkOrderReport} from "~/lib/api/reporting";

let params = useRoute().params;
let {organization_uuid, work_order_id} = params;

// --- CORE REACTIVE WORK ORDER DATA ---
let workOrder = ref(null);
const orgMembers = ref([]);

let reportForm = ref({
  summary: "",
  id: "",
  signaturePresent: false,
  cost: 0,
  duration: 0,
  duration_unit: "h",
  work_performed: ""
})

fetchMembers(organization_uuid).then(
    members => {
      orgMembers.value = members;
    }
)
fetchWorkOrder(organization_uuid, work_order_id).then(data => {
  /*
  {
  id: 4802,
  assetName: "Hydraulic Pump Station B",
  status: "planned", // Workflow: planned -> confirmed -> in_progress -> completed -> validated -> closed
  description: "Quarterly structural overhaul. Check system for hydraulic leaks, evaluate operational pressures, clean filtering modules, and swap structural seal integrity rings.",
  team: "Mechanical Maintenance Team Alpha",
  priority: "High (P2)",
  tasks: [
    {id: 1, title: "Flush core fluid chambers", assignee: "j_doe", required: true, completed: false},
    {id: 2, title: "Examine intake gauge pressure thresholds", assignee: "m_smith", required: true, completed: false},
    {id: 3, title: "Wipe down structural frame casing", assignee: "j_doe", required: false, completed: false}
  ],
  report: {
    submitted: false,
    summary: "",
    signaturePresent: false
  }
}
   */
  workOrder.value = data;
  if (data.report) {
    reportForm.value = data.report;
    // reportForm.value.summary = data.report.summary;
    console.log(reportForm.value);
    // reportForm.value.id = data.report.id;
  }
})


// --- UI & STATE HANDLERS ---
const isModalOpen = ref(false)
const reportSection = ref(null)

const newTaskForm = ref({
  title: "",
  assigneeId: "",
  required: false
})

// --- BUSINESS RULE COMPUTED PROPERTIES ---
const completedTasksCount = computed(() => {
  return workOrder.value?.tasks?.filter(t => t.status === "done").length
})

const allRequiredTasksDone = computed(() => {
  const requiredTasks = workOrder.value?.tasks?.filter(t => t.required)
  if (requiredTasks.length === 0) return true
  return requiredTasks.every(t => t.status === "done")
})

const canCompleteWorkOrder = computed(() => {
  const hasTasks = workOrder.value?.tasks?.length > 0
  const reqDone = allRequiredTasksDone.value
  const reportExists = !!workOrder.value?.report?.summary
  const reportSubmitted = workOrder.value?.report?.submitted
  const signaturesValid = workOrder.value?.report?.signaturePresent

  return hasTasks && reqDone && reportExists && reportSubmitted && signaturesValid
})

const statusBadgeClass = computed(() => {
  switch (workOrder.value.status) {
    case 'planned':
      return 'badge-ghost border-base-300 text-base-content/60'
    case 'confirmed':
      return 'badge-info text-white'
    case 'in_progress':
      return 'badge-warning text-base-content'
    case 'completed':
      return 'badge-success text-white'
    case 'validated':
      return 'badge-primary text-white'
    case 'closed':
      return 'badge-neutral'
    default:
      return 'badge-ghost'
  }
})
const _updateTaskStatus = (taskId, nextStatus) => {
  updateTaskStatus(organization_uuid, taskId, nextStatus).then(r => {
    if (r.ok) {
      workOrder.value.tasks.find(e => e.id === taskId).status = nextStatus;
    }
  })
}

// --- STATE MUTATION OPERATIONS ---
const updateStatus = (nextStatus) => {
  updateWorkOrderStatus(organization_uuid, work_order_id, nextStatus).then(r => {
    if (r.ok) {
      workOrder.value.status = nextStatus;
    }
  })
}

const completeTask = (taskId) => {
  updateTaskStatus(organization_uuid, taskId, "done").then(r => {
    if (r.ok) {
      const task = workOrder.value.tasks.find(t => t.id === taskId)
      console.log(task)
      if (task) task.status = "done"
    }
  })

}

const updateTaskProgress = (taskId, value) => {
  updateTask(organization_uuid, taskId, {task: {progress: value}}).then(r => {
    if (r.ok) {
      const task = workOrder.value.tasks.find(t => t.id === taskId)
      if (task) task.completed = true
    }
  });
  if (value === 100) {
    completeTask(taskId);
  }

}

const openReportSection = () => {
  reportSection.value?.scrollIntoView({behavior: 'smooth'})
}

const submitReport = () => {
  if (!allRequiredTasksDone.value || !reportForm.value.summary) return

  let payload = {
    id: reportForm.value.id,
    work_order_report: {
      summary: reportForm.value.summary,
      signature_present: reportForm.value.signaturePresent,
      cost: reportForm.value.cost,
      duration: reportForm.value.duration,
      duration_unit: reportForm.value.duration_unit,
      work_performed: reportForm.value.work_performed,
    }
  };
  UpsertWorkOrderReport(organization_uuid, work_order_id, payload).then((res) => {
    if (res.ok) {
      // TODO , update work order
      workOrder.value.report.submitted = true
    }
  })
}

const closeModal = () => {
  isModalOpen.value = false
  newTaskForm.value = {title: "", assigneeId: "", required: false}
}

const addTask = () => {
  if (!newTaskForm.value.title || !newTaskForm.value.assigneeId) return
  let task = createTask(organization_uuid, {
    task: {
      work_order_id,
      title: newTaskForm.value.title,
      organization_member_ids: [newTaskForm.value.assigneeId],
      required: newTaskForm.value.required
    }
  }).then((res) => {
    if (res.ok) {
      workOrder.value.tasks.push(res.data);
      closeModal();
    }
  });
}
</script>
